/* eslint-disable indent */
/* eslint-disable prettier/prettier */
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import { AxiosError } from "axios";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// Importar logos locais como fallback
import SisFreteLogo from "@/assets/logos/sisfrete.png";
import T2PayLogo from "@/assets/logos/Talk2Pay_256X256.png";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import useLanguage from "@/contexts/language-context";
import useToast from "@/contexts/toast-context";
import { getAccessToken } from "@/helpers/auth-helpers";
import { isCustomerPlatformBindingActive } from "@/helpers/customer-platform-binding.helpers";
import useGetCustomerId from "@/hooks/use-get-customer-id";
import useLoading from "@/hooks/use-loading";
import { ICustomerPlatform } from "@/interfaces/customer-platform.interfaces";
import { IPlatform } from "@/interfaces/platform.interfaces";
import { PlatformsMessages } from "@/messages/platforms/platforms.messages";
import customerPlatformService from "@/services/customer-platform.service";
import lojaIntegradaService from "@/services/loja-integrada.service";

import { PlatformConsultModal } from "../PlatformConsultModal";

interface Props {
  platform: IPlatform;
  messages: PlatformsMessages;
}
interface ActionButton {
  active: boolean;
  connected: boolean;
  /** Vínculo existe mas Active=false/0 na API: mostra "Desconectar" desabilitado */
  canDisconnect: boolean;
  platformId: number;
}

/** Plataformas com fluxo de onboarding próprio no frontend (retrocompatibilidade). */
const PLATFORM_IDS_WITH_ONBOARDING_FLOW = [1, 2, 3, 5];

function hasOnboardingCode(platform: IPlatform): boolean {
  if (platform.onboardingCode) return true;
  return PLATFORM_IDS_WITH_ONBOARDING_FLOW.includes(platform.id);
}

/** Só WhatsApp Meta e Gupshup/BSP usam a regra de desconectar cinza quando o vínculo está inativo */
function isWhatsappOrGupshupPlatformCard(p: IPlatform): boolean {
  const name = p.name?.toUpperCase() ?? "";
  const display = p.displayName?.toUpperCase() ?? "";
  if (p.id === 1 || p.id === 5) return true;
  if (name === "WHATSAPP_BSP" || name === "GUPSHUP") return true;
  if (display === "WHATSAPP BSP" || display.includes("WHATSAPP BSP")) return true;
  return false;
}

function getRequestErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    const data = error.response?.data;
    if (Array.isArray(data?.messages) && data.messages.length > 0) return data.messages.join(" ");
    if (typeof data?.message === "string") return data.message;
    return error.message;
  }
  return (error as Error)?.message ?? "Erro ao desconectar";
}

const DefaultPlatformCard = ({ platform, messages }: Props) => {
  const { lang } = useLanguage();
  const router = useNavigate();
  const customerId = useGetCustomerId();
  const accessToken = getAccessToken();
  const { showToast } = useToast();
  const [connectedPlatforms, setConnectedPlatforms] = useState<ICustomerPlatform[]>([]);
  const { startLoading, endLoading, isLoading } = useLoading();
  const [imageError, setImageError] = useState(false);
  const [isConsultModalOpen, setIsConsultModalOpen] = useState(false);
  const isWhatsappUnsubscribingRef = useRef(false);

  // Função para obter o logo correto
  const getPlatformLogo = () => {
    if (imageError || !platform.logoImage) {
      // Fallback para logo local baseado no platformId
      if (platform.id === 2) {
        // T2Pay
        return T2PayLogo;
      }

      // Fallbacks baseados no nome da plataforma ou ID
      const platformName = platform.name?.toUpperCase();
      switch (platformName || platform.id) {
        case "SISFRETE":
          return SisFreteLogo;
        case "WHATSAPP":
        case "WHATSAPP_BSP":
        case 1: // WhatsApp platformId
          return "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjU2IiBoZWlnaHQ9IjI1NiIgdmlld0JveD0iMCAwIDI1NiAyNTYiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyNTYiIGhlaWdodD0iMjU2IiByeD0iMTI4IiBmaWxsPSIjMjVEMzY2Ii8+Cjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSJ3aGl0ZSIgZm9udC1zaXplPSI0OCIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXdlaWdodD0iYm9sZCI+VzwvdGV4dD4KPC9zdmc+";
        case "LOJA INTEGRADA":
        case 3: // Loja Integrada platformId (se existir)
          return "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjU2IiBoZWlnaHQ9IjI1NiIgdmlld0JveD0iMCAwIDI1NiAyNTYiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyNTYiIGhlaWdodD0iMjU2IiByeD0iMTI4IiBmaWxsPSIjNEZBNUY1Ii8+Cjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSJ3aGl0ZSIgZm9udC1zaXplPSI0OCIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXdlaWdodD0iYm9sZCI+TDwvdGV4dD4KPC9zdmc+";
        default:
          return T2PayLogo; // Usar T2Pay como padrão universal
      }
    }
    return `data:image/jpeg;base64,${platform.logoImage}`;
  };

  const handleImageError = () => {
    setImageError(true);
  };

  useEffect(() => {
    const fetchConnectedPlatforms = async () => {
      startLoading();
      if (!accessToken || !customerId) {
        endLoading();
        return;
      }
      const response = await customerPlatformService.getAll();
      setConnectedPlatforms(response);
      endLoading();
    };
    fetchConnectedPlatforms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, customerId]);

  // Navegação para as rotas dos módulos (igual ao Next)
  const handleClickWhatsappConnection = () => {
    router(`/settings/platforms/whatsapp/${lang}`);
  };

  const handleClickWhatsappBspConnection = () => {
    router(`/settings/platforms/whatsapp-bsp/${lang}`);
  };

  const handleClickWhatsappBspUnsubscribe = async () => {
    if (!accessToken || !customerId) return;

    const whatsappBspConnection = connectedPlatforms.find(
      (platform) => 
        (platform.platformId === 5) ||
        (platform.platformId && platform.platformId.toString() === "5")
    );

    if (!whatsappBspConnection) return;

    const {
      id: customerPlatformId,
      customerExternalId: appId,
    } = whatsappBspConnection;

    if (!appId) return;

    try {
      startLoading();
      await customerPlatformService.gupshupUnsubscribe(appId, customerPlatformId);
      showToast({
        duration: 5000,
        text: "WhatsApp BSP desconectado com sucesso",
        type: "success",
      });
    } catch (error) {
      const { message } = error as Error;

      if (error instanceof AxiosError) {
        showToast({ 
          duration: 10000, 
          text: error.response?.data?.message || JSON.stringify(error?.response?.data) || message, 
          type: "error" 
        });
        return;
      }

      showToast({ text: message, type: "error" });
    } finally {
      endLoading();
      const response = await customerPlatformService.getAll();
      setConnectedPlatforms(response);
    }
  };

  const handleClickT2bPayConnection = () => {
    router(`/settings/platforms/talk2pay/${lang}`);
  };

  const handleClickT2bPayHome = () => {
    router(`/settings/platforms/talk2pay/home/${lang}`);
  };

  const handleClickLojaIntegradaConnection = () => {
    router(`/settings/platforms/loja-integrada/${lang}`);
  };

  const handleClickWhatsappUnsubscribe = async () => {
    if (!accessToken || !customerId) return;

    const whatsappConnection = connectedPlatforms.find(
      (platform) => platform.platformId === 1 && platform.customerExternalId !== "5511963753732" // talk2buy
    );

    if (!whatsappConnection) return;

    const {
      id: customerPlatformId,
      customerExternalId: wabaId,
      platformProfileId: phoneNumberId,
    } = whatsappConnection;

    if (isWhatsappUnsubscribingRef.current) return;
    isWhatsappUnsubscribingRef.current = true;
    startLoading();

    let unsubscribeError: unknown = null;
    try {
      await customerPlatformService.whatsappUnsubscribe(
        accessToken,
        customerId,
        customerPlatformId,
        Number(wabaId),
        Number(phoneNumberId)
      );
    } catch (error) {
      unsubscribeError = error;
    }

    try {
      const response = await customerPlatformService.getAll();
      setConnectedPlatforms(response);

      const stillConnected = response.some((cp) => cp.id === customerPlatformId);
      if (!stillConnected) {
        showToast({ duration: 5000, text: "WhatsApp desconectado com sucesso", type: "success" });
      } else if (unsubscribeError) {
        showToast({ duration: 10000, text: getRequestErrorMessage(unsubscribeError), type: "error" });
      }
    } finally {
      isWhatsappUnsubscribingRef.current = false;
      endLoading();
    }
  };

  const handleClickLojaIntegradaUnsubscribe = async () => {
    try {
      if (!customerId) return;
      await lojaIntegradaService.lojaIntegradaUnsubscribe();
      const response = await customerPlatformService.getAll();
      setConnectedPlatforms(response);
    } catch (error) {
      const { message } = error as Error;

      if (error instanceof AxiosError) {
        showToast({ duration: 10000, text: JSON.stringify(error?.response?.data), type: "error" });
        return;
      }

      showToast({ text: message, type: "error" });
    }
  };

  const renderActionButton = ({ active, connected, canDisconnect, platformId }: ActionButton) => {
    if (!active) {
      return (
        <MDButton variant="gradient" color="dark" size="small" disabled>
          {messages.soonButtonLabel}
        </MDButton>
      );
    }

    if (!hasOnboardingCode(platform)) {
      return (
        <MDButton
          variant="gradient"
          color="dark"
          size="small"
          onClick={() => setIsConsultModalOpen(true)}
        >
          {messages.consultButtonLabel}
        </MDButton>
      );
    }

    const platformNameUpper = platform.name?.toUpperCase() || "";
    const displayNameUpper = platform.displayName?.toUpperCase() || "";
    const isWhatsAppBsp = 
      platformNameUpper === "WHATSAPP_BSP" || 
      platformNameUpper === "GUPSHUP" ||
      displayNameUpper === "WHATSAPP BSP" ||
      displayNameUpper.includes("WHATSAPP BSP");

    switch (platformId) {
      case 1:
        if (!connected)
          return (
            <MDButton
              variant="gradient"
              color="dark"
              size="small"
              onClick={handleClickWhatsappConnection}
              isLoading={isLoading}
            >
              {messages.connectButtonLabel}
            </MDButton>
          );

        return (
          <MDButton
            variant="gradient"
            color="dark"
            size="small"
            onClick={handleClickWhatsappUnsubscribe}
            isLoading={isLoading}
            disabled={!canDisconnect}
          >
            {messages.disconnectButtonLabel}
          </MDButton>
        );

      case 2:
        if (!connected)
          return (
            <MDButton
              variant="gradient"
              color="dark"
              size="small"
              onClick={handleClickT2bPayConnection}
              isLoading={isLoading}
            >
              {messages.connectButtonLabel}
            </MDButton>
          );

        return (
          <MDButton
            variant="gradient"
            color="dark"
            size="small"
            onClick={handleClickT2bPayHome}
            isLoading={isLoading}
          >
            {messages.configButtonLabel}
          </MDButton>
        );

      case 3:
        if (!connected)
          return (
            <MDButton
              variant="gradient"
              color="dark"
              size="small"
              onClick={handleClickLojaIntegradaConnection}
              isLoading={isLoading}
            >
              {messages.connectButtonLabel}
            </MDButton>
          );

        return (
          <MDButton
            variant="gradient"
            color="dark"
            size="small"
            onClick={handleClickLojaIntegradaUnsubscribe}
            isLoading={isLoading}
            disabled={!canDisconnect}
          >
            {messages.disconnectButtonLabel}
          </MDButton>
        );

      case 5:
        if (!connected)
          return (
            <MDButton
              variant="gradient"
              color="dark"
              size="small"
              onClick={handleClickWhatsappBspConnection}
              isLoading={isLoading}
            >
              {messages.connectButtonLabel}
            </MDButton>
          );

        return (
          <MDButton
            variant="gradient"
            color="dark"
            size="small"
            onClick={handleClickWhatsappBspUnsubscribe}
            isLoading={isLoading}
            disabled={!canDisconnect}
          >
            {messages.disconnectButtonLabel}
          </MDButton>
        );

      default:
        if (isWhatsAppBsp) {
          if (!connected)
            return (
              <MDButton
                variant="gradient"
                color="dark"
                size="small"
                onClick={handleClickWhatsappBspConnection}
                isLoading={isLoading}
              >
                {messages.connectButtonLabel}
              </MDButton>
            );

          return (
            <MDButton
              variant="gradient"
              color="dark"
              size="small"
              onClick={handleClickWhatsappBspUnsubscribe}
              isLoading={isLoading}
              disabled={!canDisconnect}
            >
              {messages.disconnectButtonLabel}
            </MDButton>
          );
        }

        return (
          <MDButton variant="gradient" color="dark" size="small" disabled isLoading={isLoading}>
            {messages.soonButtonLabel}
          </MDButton>
        );
    }
  };

  const customerConnection = connectedPlatforms.find(
    (cp: ICustomerPlatform) => cp.platformId === platform.id
  );
  const isConnected = !!customerConnection;
  const canDisconnect =
    isConnected &&
    !!customerConnection &&
    (!isWhatsappOrGupshupPlatformCard(platform) ||
      isCustomerPlatformBindingActive(customerConnection));

  return (
    <>
      <Card sx={{ height: "100%", width: "100%", display: "flex", flexDirection: "column" }}>
        <MDBox p={2} mx={3} display="flex" justifyContent="center">
          <MDBox
            display="grid"
            justifyContent="center"
            alignItems="center"
            color="white"
            borderRadius="lg"
            variant="gradient"
          >
            <img
              src={getPlatformLogo()}
              style={{ objectFit: "contain" }}
              height={80}
              width={100}
              alt={`${platform.name} logo`}
              onError={handleImageError}
            />
          </MDBox>
        </MDBox>
        <MDBox
          pb={2}
          px={2}
          textAlign="center"
          lineHeight={1.25}
          sx={{ flex: 1, display: "flex", flexDirection: "column" }}
        >
          <MDTypography
            variant="h6"
            fontWeight="medium"
            textTransform="capitalize"
            sx={{
              minHeight: "3.2em",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {platform.displayName}
          </MDTypography>

          <Divider />

          <MDBox mt="auto" pt={1}>
            {renderActionButton({
              active: platform.active,
              connected: isConnected,
              canDisconnect,
              platformId: platform.id,
            })}
          </MDBox>
        </MDBox>
      </Card>

      <PlatformConsultModal
        isOpen={isConsultModalOpen}
        onClose={() => setIsConsultModalOpen(false)}
        platformName={platform.displayName}
        messages={messages}
      />
    </>
  );
};

export default DefaultPlatformCard;
